import Grid from '@mui/material/Grid'
import { LargeCard } from 'Components/Cards/Large'
import Typography from '@mui/material/Typography/Typography'
import Button from '@mui/material/Button'
import { duplicateSource, type ChildDuplicate } from 'slices/duplicateChildSlice'
import { Alert, Box, Stack } from '@mui/material'
import { DupePanel } from 'Components/ChildDetails/ChildDuplicateDisplay'
import LaunchIcon from '@mui/icons-material/Launch'
import { generatePath, useNavigate } from 'react-router-dom'
import { CARENETWORK_MANAGEMENT_DETAILS } from 'Pages/Routes'
import { CaregiverDetailsDisplay } from 'Components/CareNetwork/CaregiverDetailsDisplay'
import ButtonGroup from 'Components/Button/ButtonGroup'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { type DenialAccessPermissions } from 'Components/ChildDetails/DuplicateResolution'
import { UserDetailsDisplay } from 'Components/CareNetwork/UserDetailsDisplay'
import { useDispatch } from 'react-redux'
import { updateAccessPermissions } from 'slices/duplicateChildSlice'
import { usePermissions } from 'hooks/use-permissions'

interface Props {
  dupeRecord: ChildDuplicate | null
  existingRecord: ChildDuplicate | null
  accessPermissions: DenialAccessPermissions
  onSubmit: () => Promise<void>
  goBack: () => void
}

export function DiscardDuplicate (props: Props): JSX.Element {
  const { dupeRecord, existingRecord, accessPermissions, onSubmit, goBack } = props
  const nav = useNavigate()
  const dispatch = useDispatch()
  const perms = usePermissions()

  const loadExistingRecord = (): void => {
    if (existingRecord?.childId != null) {
      const path = generatePath(CARENETWORK_MANAGEMENT_DETAILS, { id: existingRecord?.childId.toString() ?? '' })
      nav(path)
    }
  }

  if (dupeRecord == null || existingRecord == null) {
    return <LargeCard fullWidth content={
      <Typography>Duplicate information was not loaded correctly. Please go back and try again.</Typography>
    }
    />
  }
  const descriptionText = dupeRecord.source === duplicateSource.CaregiverPortal
    ? 'This duplicate was created by a caregiver via the interest form process. Please decide if you would like to grant this caregiver access to original child record.'
    : 'Please decide if you would like to grant this user access to original child record.'

  return <LargeCard customContentWidth fullWidth
    contentSx={{ p: '20px' }}
    sx={{ width: 'fit-content', borderRadius: '15px' }}
    content={
      <Stack direction='column' spacing={2}>
        <Typography fontWeight={'bold'} fontSize={'18px'}>Resolve Child Record Access</Typography>
        <Alert severity='info' sx={{ borderRadius: '12px', border: '1px solid' }}>{descriptionText}</Alert>
        <Grid container columnSpacing={2}>
          <Grid item xs={6}>
            {dupeRecord.requestingUserIsCaregiver
              ? <CaregiverDetailsDisplay
                  caregiverID={Number.parseInt(dupeRecord.requestingUserID)}
                  caregiver={{
                    firstName: dupeRecord.requestingUserFirstName,
                    lastName: dupeRecord.requestingUserLastName,
                    relationship: dupeRecord.requestingUserRelationship,
                    isFinanciallyResponsible: dupeRecord.requestingUserIsFinanciallyResponsible,
                    livesWithChild: dupeRecord.requestingUserLivesWithChild,
                    primaryLanguage: dupeRecord.requestingUserPrimaryLanguage,
                    phone: dupeRecord.requestingUserPhone,
                    email: dupeRecord.requestingUserEmail,
                    dateOfBirth: dupeRecord.requestingUserDob,
                    address: dupeRecord.requestingUserAddress,
                    city: dupeRecord.requestingUserCity,
                    state: dupeRecord.requestingUserState,
                    zip: dupeRecord.requestingUserZip,
                    childFirstName: dupeRecord.firstName,
                    s3Key: dupeRecord.requestingUserS3Key
                  }}
              />
              : <UserDetailsDisplay user={{
                firstName: dupeRecord.requestingUserFirstName,
                lastName: dupeRecord.requestingUserLastName,
                isdName: dupeRecord.requestingUserISDName,
                securityLevel: dupeRecord.requestingUserSecurityLevel,
                phone: dupeRecord.requestingUserPhone,
                email: dupeRecord.requestingUserEmail,
                s3Key: dupeRecord.requestingUserS3Key
              }}
              />}
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', px: '10px', gap: 1 }} data-testid='existingRecordContainer'>
              <Typography fontWeight={'bold'}>Existing Record</Typography>
              <DupePanel {...existingRecord} />
              {existingRecord.childId != null && existingRecord.childId !== 0 && perms.CareNetwork && (
                <Button variant='text'
                  onClick={loadExistingRecord}
                  sx={{ width: 'fit-content', alignSelf: 'center', textTransform: 'none', textDecoration: 'underline', color: 'black' }}
                >
                  See Full Child Record
                  <LaunchIcon />
                </Button>
              )}
              <ButtonGroup
                value={accessPermissions.grantAccess ? 1 : 0}
                options={[{ id: 0, name: 'Deny Child Access' }, { id: 1, name: 'Grant Child Access' }]}
                setValue={(value) => { dispatch(updateAccessPermissions({ grantAccess: value === 1, portalView: false, portalModify: false })) }}
              />
              {dupeRecord.requestingUserIsCaregiver && accessPermissions.grantAccess && <Stack direction='row' spacing={1}>
                <CheckBoxWithLabel
                  name='portalViewCheckbox'
                  label='Portal View'
                  setValue={(value) => { dispatch(updateAccessPermissions({ ...accessPermissions, portalView: value, portalModify: value ? accessPermissions.portalModify : false })) }}
                  value={accessPermissions.portalView}
                  edge='start'
                />
                <CheckBoxWithLabel
                  name='portalModifyCheckbox'
                  label='Portal Modify'
                  setValue={(value) => { dispatch(updateAccessPermissions({ ...accessPermissions, portalView: value ? true : accessPermissions.portalView, portalModify: value })) }}
                  value={accessPermissions.portalModify}
                  edge='start'
                />
              </Stack>}
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Button sx={{ textTransform: 'none', px: '35px' }} variant='outlined' onClick={goBack}>Back</Button>
          <Button sx={{ textTransform: 'none', px: '35px' }} variant='contained' onClick={onSubmit} data-testid='submit-button'>Submit</Button>
        </Box>
      </Stack>}
  />
}
